import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { pricing, wsSummary, roleSummary, taskEffort, taskCost, locMap, fmtH, fmtC } from "@/lib/calc";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Th = ({ children, right }) => (
  <th className={`bg-zinc-50 border-b border-zinc-300 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, mono = true, className = "" }) => (
  <td className={`border-b border-zinc-100 px-3 py-2 text-[13px] ${mono ? "font-mono" : ""} ${right ? "text-right" : "text-left"} ${className}`}>{children}</td>
);
const Delta = ({ v, fmt = fmtH, suffix = "" }) => {
  if (Math.abs(v) < 0.05) return <span className="text-zinc-300">—</span>;
  return <span className={v > 0 ? "text-red-600" : "text-emerald-600"}>{v > 0 ? "+" : "−"}{fmt(Math.abs(v))}{suffix}</span>;
};

// Cost of a task INCLUDING its contingency, using the given estimation's roles/sizes/location map.
const taskCostIncCont = (t, est, locs) =>
  taskCost(t, est.roles, est.tshirt_sizes, locs) * (1 + (Number(t.contingency_pct) || 0));

export default function BaselineTab({ est, oppId }) {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState("");
  const [baseline, setBaseline] = useState(null);
  const [label, setLabel] = useState("");

  const loadList = () => api.get(`/opportunities/${oppId}/baselines`).then((r) => {
    setList(r.data);
    if (r.data.length && !selected) setSelected(r.data[0].id);
  }).catch((e) => toast.error(apiError(e)));

  useEffect(() => { loadList(); }, [oppId]); // eslint-disable-line
  useEffect(() => {
    if (!selected) { setBaseline(null); return; }
    api.get(`/opportunities/${oppId}/baselines/${selected}`).then((r) => setBaseline(r.data)).catch(() => setBaseline(null));
  }, [selected, oppId]); // eslint-disable-line

  const snap = async () => {
    try {
      const { data } = await api.post(`/opportunities/${oppId}/baselines`, { label });
      setLabel("");
      toast.success(`Baseline "${data.label}" saved`);
      setList([data, ...(list || [])]);
      setSelected(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/opportunities/${oppId}/baselines/${id}`);
      const next = list.filter((b) => b.id !== id);
      setList(next);
      if (selected === id) setSelected(next[0]?.id || "");
    } catch (e) { toast.error(apiError(e)); }
  };

  const bEst = baseline?.estimation;
  const cur = pricing(est);
  const base = bEst ? pricing(bEst) : null;
  const curLocs = locMap(est);
  const baseLocs = bEst ? locMap(bEst) : {};

  // task diffs by name
  let added = [], removed = [], changed = [];
  if (bEst) {
    const bByName = Object.fromEntries(bEst.tasks.map((t) => [t.name.toLowerCase(), t]));
    const cByName = Object.fromEntries(est.tasks.map((t) => [t.name.toLowerCase(), t]));
    added = est.tasks.filter((t) => !bByName[t.name.toLowerCase()]);
    removed = bEst.tasks.filter((t) => !cByName[t.name.toLowerCase()]);
    changed = est.tasks.filter((t) => {
      const b = bByName[t.name.toLowerCase()];
      if (!b) return false;
      const eC = taskEffort(t, est.tshirt_sizes), eB = taskEffort(b, bEst.tshirt_sizes);
      if (Math.abs(eC - eB) > 0.05) return true;
      if (Math.abs((t.contingency_pct || 0) - (b.contingency_pct || 0)) > 0.001) return true;
      // Cost may also shift without effort change (role split / location / rate changes)
      const cC = taskCostIncCont(t, est, curLocs);
      const cB = taskCostIncCont(b, bEst, baseLocs);
      return Math.abs(cC - cB) > 0.5;
    }).map((t) => ({ cur: t, base: bByName[t.name.toLowerCase()] }));
  }

  return (
    <div data-testid="baseline-tab">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input data-testid="baseline-label-input" value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Baseline label (optional)…"
          className="border border-zinc-300 rounded-sm px-3 py-2 text-sm w-64 outline-none focus:border-[#002FA7]" />
        <button data-testid="save-baseline-button" onClick={snap}
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium px-4 py-2 rounded-sm">
          <Camera size={14} strokeWidth={1.5} /> Save baseline now
        </button>
        {list?.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-zinc-500">Compare against</span>
            <select data-testid="baseline-select" value={selected} onChange={(e) => setSelected(e.target.value)}
              className="text-xs border border-zinc-300 rounded-sm px-2.5 py-2 bg-white outline-none focus:border-[#002FA7] max-w-[320px]">
              {list.map((b) => <option key={b.id} value={b.id}>{b.label} · {new Date(b.created_at).toLocaleDateString()}</option>)}
            </select>
            <button data-testid="delete-baseline-button" onClick={() => remove(selected)} className="text-zinc-400 hover:text-red-600 p-1.5 border border-zinc-200 rounded-sm">
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>

      {!list ? <p className="text-sm text-zinc-500">Loading…</p> : list.length === 0 ? (
        <div data-testid="baseline-empty-state" className="border border-dashed border-zinc-300 rounded-sm p-12 text-center">
          <p className="text-sm text-zinc-500 mb-1">No baselines yet.</p>
          <p className="text-xs text-zinc-400">Save a baseline to lock in the current estimate. Baselines are also captured automatically on AI generation and when the opportunity is marked Submitted.</p>
        </div>
      ) : !baseline || !base ? <p className="text-sm text-zinc-500">Loading baseline…</p> : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 rounded-sm overflow-hidden">
            {[
              ["Load hrs", base.loadHours, cur.loadHours, fmtH],
              ["Load cost", base.subtotal, cur.subtotal, fmtC],
              ["WBS suggested", base.suggestedCost, cur.suggestedCost, fmtC],
              ["Final price", base.finalPrice, cur.finalPrice, fmtC],
            ].map(([l, b, c, f]) => (
              <div key={l} className="bg-white p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500">{l}</p>
                <p className="font-mono text-lg mt-1">{f(c)}</p>
                <p className="text-[11px] font-mono text-zinc-400">baseline {f(b)} · <Delta v={c - b} fmt={f} /></p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <div className="bg-white border border-zinc-200 rounded-sm">
              <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Deviation by role <span className="text-zinc-400 font-normal">(hrs incl. contingency)</span></h3></div>
              <table className="w-full">
                <thead><tr><Th>Role</Th><Th right>Baseline</Th><Th right>Current</Th><Th right>Δ</Th></tr></thead>
                <tbody>
                  {(() => {
                    const bR = Object.fromEntries(roleSummary(bEst).map((r) => [r.name, r.hours]));
                    const cR = Object.fromEntries(roleSummary(est).map((r) => [r.name, r.hours]));
                    const names = [...new Set([...Object.keys(bR), ...Object.keys(cR)])];
                    return names.map((n) => (
                      <tr key={n} className="hover:bg-zinc-50">
                        <Td mono={false} className="text-xs">{n}</Td>
                        <Td right>{fmtH(bR[n] || 0)}</Td><Td right>{fmtH(cR[n] || 0)}</Td>
                        <Td right><Delta v={(cR[n] || 0) - (bR[n] || 0)} /></Td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm">
              <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Deviation by workstream <span className="text-zinc-400 font-normal">(cost incl. contingency)</span></h3></div>
              <table className="w-full">
                <thead><tr><Th>Workstream</Th><Th right>Baseline</Th><Th right>Current</Th><Th right>Δ</Th></tr></thead>
                <tbody>
                  {(() => {
                    const bW = Object.fromEntries(wsSummary(bEst).map((w) => [w.name, w.totalCost]));
                    const cW = Object.fromEntries(wsSummary(est).map((w) => [w.name, w.totalCost]));
                    const names = [...new Set([...Object.keys(bW), ...Object.keys(cW)])];
                    return names.map((n) => (
                      <tr key={n} className="hover:bg-zinc-50">
                        <Td mono={false} className="text-xs">{n}</Td>
                        <Td right>{fmtC(bW[n] || 0)}</Td><Td right>{fmtC(cW[n] || 0)}</Td>
                        <Td right><Delta v={(cW[n] || 0) - (bW[n] || 0)} fmt={fmtC} /></Td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm" data-testid="baseline-task-changes">
            <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Scope changes since baseline</h3></div>
            {added.length + removed.length + changed.length === 0 ? (
              <p className="px-4 py-6 text-xs text-zinc-400">No task-level scope changes.</p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {added.map((t) => {
                  const cost = taskCostIncCont(t, est, curLocs);
                  return (
                    <div key={t.id} className="px-4 py-2 flex items-center justify-between gap-4">
                      <p className="text-[13px] min-w-0 truncate"><span className="text-emerald-600 font-medium text-xs mr-2">ADDED</span>{t.name} <span className="text-zinc-400 text-xs">({t.workstream})</span></p>
                      <p className="font-mono text-xs whitespace-nowrap shrink-0">
                        <span>+{fmtH(taskEffort(t, est.tshirt_sizes))} hrs</span>
                        <span className="text-zinc-300 mx-2">·</span>
                        <span className="text-red-600">+{fmtC(cost)}</span>
                      </p>
                    </div>
                  );
                })}
                {removed.map((t) => {
                  const cost = taskCostIncCont(t, bEst, baseLocs);
                  return (
                    <div key={t.id || t.name} className="px-4 py-2 flex items-center justify-between gap-4">
                      <p className="text-[13px] min-w-0 truncate"><span className="text-red-600 font-medium text-xs mr-2">REMOVED</span>{t.name} <span className="text-zinc-400 text-xs">({t.workstream})</span></p>
                      <p className="font-mono text-xs whitespace-nowrap shrink-0">
                        <span>−{fmtH(taskEffort(t, bEst.tshirt_sizes))} hrs</span>
                        <span className="text-zinc-300 mx-2">·</span>
                        <span className="text-emerald-600">−{fmtC(cost)}</span>
                      </p>
                    </div>
                  );
                })}
                {changed.map(({ cur: t, base: b }) => {
                  const cC = taskCostIncCont(t, est, curLocs);
                  const cB = taskCostIncCont(b, bEst, baseLocs);
                  const dC = cC - cB;
                  return (
                    <div key={t.id} className="px-4 py-2 flex items-center justify-between gap-4">
                      <p className="text-[13px] min-w-0 truncate"><span className="text-amber-600 font-medium text-xs mr-2">CHANGED</span>{t.name}</p>
                      <p className="font-mono text-xs whitespace-nowrap shrink-0">
                        <span>{fmtH(taskEffort(b, bEst.tshirt_sizes))} → {fmtH(taskEffort(t, est.tshirt_sizes))} hrs</span>
                        {Math.abs((t.contingency_pct || 0) - (b.contingency_pct || 0)) > 0.001 && <span className="text-zinc-500"> · cont {Math.round((b.contingency_pct || 0) * 100)}%→{Math.round((t.contingency_pct || 0) * 100)}%</span>}
                        <span className="text-zinc-300 mx-2">·</span>
                        <span className={dC > 0 ? "text-red-600" : dC < 0 ? "text-emerald-600" : "text-zinc-400"}>
                          {fmtC(cB)} → {fmtC(cC)} ({dC > 0 ? "+" : dC < 0 ? "−" : ""}{fmtC(Math.abs(dC))})
                        </span>
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
